import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { UIFeedbackService } from '@shared/services/ui-feedback.service';
import { firstValueFrom } from 'rxjs';

import { COCKPIT_STORE, type ApproveResult } from '@app/pages/apps/cockpit/store/cockpit-store.interface';
import { type ActionCard, getCardEffects } from '@shared/definitions/action-card.model';
import { SnoozeDateDialogComponent } from '../components/dialogs/snooze-date-dialog.component';
import {
  StaleCardDialogComponent,
  type StaleCardDialogResult,
} from '../components/dialogs/stale-card-dialog.component';

/** Outcome a caller can react to (e.g. open the detail drawer on failure). */
export type ConfirmOutcome = 'ok' | 'stale-handled' | 'failed' | 'error';

/**
 * Shared card actions (confirm & send / defer / dismiss) with their toasts,
 * undo affordances and stale-card handling — the exact behaviour the detail
 * panel offers, extracted so the dashboard's inline quick actions and the light
 * preview dialog drive the SAME store path. Crucially it handles the backend
 * `stale` verdict (rent already paid, indexation applied) that the old
 * dashboard quick-approve dropped silently.
 */
/**
 * NOTE: deliberately NOT `providedIn: 'root'`. This service injects
 * `COCKPIT_STORE`, a route-scoped token wired by `provideCockpitStore()`.
 * Marking it root-provided made Angular instantiate it in the root injector,
 * where `COCKPIT_STORE` does not exist → NG0201 crashed the whole cockpit on
 * load. It is provided alongside the store in `provideCockpitStore()` so it
 * lives in the same route injector.
 */
@Injectable()
export class CockpitCardActionsService {
  private readonly store = inject(COCKPIT_STORE);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly t = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  /**
   * Confirm a simple card and execute its enabled effects. Re-checks relevance
   * server-side: a `stale` verdict opens the warning dialog (send anyway / file
   * away) instead of silently sending. Returns an outcome the caller can use to
   * decide whether to surface the detail drawer.
   */
  async confirmAndSend(card: ActionCard, force = false): Promise<ConfirmOutcome> {
    try {
      const result = await this.store.approveWithEffects(
        card.id,
        undefined,
        undefined,
        force ? { force: true } : undefined,
      );
      if (result && result.status === 'stale') {
        await this.handleStale(card, result);
        return 'stale-handled';
      }
      return this.toastApproveResult(card, result);
    } catch (err) {
      this.toastApproveError(err);
      return 'error';
    }
  }

  /** Snooze to an explicit instant (preset menu); toast with undo. */
  snooze(card: ActionCard, untilIso: string): void {
    void this.store.snooze(card.id, untilIso);
    this.toast(
      'cockpit.snooze.toast.deferred',
      'cockpit.action.undo',
      () => void this.store.reopen(card.id),
      4000,
      { date: this.formatWake(untilIso) },
    );
  }

  /** Open the custom-date picker, then snooze to the chosen instant. */
  async snoozeCustom(card: ActionCard): Promise<void> {
    const iso = (await firstValueFrom(
      this.dialog.open(SnoozeDateDialogComponent, { width: '360px' }).afterClosed(),
    )) as string | undefined;
    if (iso) this.snooze(card, iso);
  }

  /**
   * Dismiss a card. On a `scheduled_auto` card this cancels the pending
   * auto-send (mirrors `cancelScheduledCard`); both drop the card to `rejected`
   * via the same reject path with a distinct reason. Toast with undo.
   */
  dismiss(card: ActionCard): void {
    const auto = card.status === 'scheduled_auto';
    const reasonKey = auto ? 'cockpit.reject.autoCancelledReason' : 'cockpit.reject.notRelevantReason';
    const toastKey = auto ? 'cockpit.toast.autoCancelled' : 'cockpit.toast.rejected';
    void this.store.reject(card.id, this.t.instant(reasonKey));
    this.toast(toastKey, 'cockpit.action.undo', () => void this.store.reopen(card.id));
  }

  /* ------------------------------------------------------------------ */

  private async handleStale(card: ActionCard, result: ApproveResult): Promise<void> {
    const reason = result.staleReason;
    const choice = (await firstValueFrom(
      this.dialog
        .open(StaleCardDialogComponent, {
          width: '460px',
          data: { reasonCode: reason?.code ?? 'generic', detail: reason?.detail },
        })
        .afterClosed(),
    )) as StaleCardDialogResult | undefined;

    if (choice === 'force') {
      await this.confirmAndSend(card, true);
    } else if (choice === 'dismiss') {
      void this.store.resolveNoLongerRelevant(card.id, reason?.code ?? 'unknown');
      this.toast('cockpit.relevance.toast.dismissed', 'cockpit.action.undo', () =>
        void this.store.reopen(card.id),
      );
    }
  }

  /** Highest trust tier across enabled effects — drives the undo window. */
  private highestTrust(card: ActionCard): 'low' | 'medium' | 'high' {
    const enabled = getCardEffects(card).filter((e) => e.enabled);
    if (enabled.some((e) => e.trustTier === 'high')) return 'high';
    if (enabled.some((e) => e.trustTier === 'medium')) return 'medium';
    return 'low';
  }

  private toastApproveResult(card: ActionCard, result: ApproveResult | void): ConfirmOutcome {
    if (!result) {
      const undoDuration = this.highestTrust(card) === 'low' ? 4000 : 8000;
      this.toast('cockpit.toast.executed', 'cockpit.action.undo', () => void this.store.reopen(card.id), undoDuration);
      return 'ok';
    }
    if (result.status === 'failed' || (result.executedEffects.length === 0 && result.failedEffects.length > 0)) {
      const first = result.failedEffects[0]?.message;
      const msg = first
        ? `${this.t.instant('cockpit.toast.failed')} — ${first}`
        : this.t.instant('cockpit.toast.failed');
      this.uiFeedback.toast.error(msg, { duration: 6000 });
      return 'failed';
    }
    if (result.failedEffects.length > 0) {
      this.uiFeedback.toast.warning(
        this.t.instant('cockpit.toast.partial', {
          ok: result.executedEffects.length,
          ko: result.failedEffects.length,
        }),
        { duration: 6000 },
      );
      return 'failed';
    }
    if (result.status === 'waiting_external') {
      this.toast('cockpit.toast.waiting');
      return 'ok';
    }
    const undoDuration = this.highestTrust(card) === 'low' ? 4000 : 8000;
    // Undo is `reopen()`: `status: 'pending'`, nothing else. When the approve
    // was the workflow's LAST step, `workflow.status` stays `completed` — the
    // card would come back red in the cockpit while its own page reads
    // « Clôturé », with no action left to close it. No Undo on that one.
    if (result.workflowStatus?.workflowCompleted) {
      this.toast('cockpit.toast.executed', undefined, undefined, undoDuration);
      return 'ok';
    }
    this.toast('cockpit.toast.executed', 'cockpit.action.undo', () => void this.store.reopen(card.id), undoDuration);
    return 'ok';
  }

  private toastApproveError(err: unknown): void {
    const msg =
      err instanceof Error && err.message === 'NO_WORKSPACE'
        ? this.t.instant('cockpit.toast.noWorkspace')
        : this.t.instant('cockpit.toast.failed');
    this.uiFeedback.toast.error(msg, { duration: 6000 });
    if (err) console.error('[cockpit] approve failed', err);
  }

  private formatWake(iso: string): string {
    const lang = this.t.currentLang() || this.t.getFallbackLang() || 'fr';
    try {
      return new Date(iso).toLocaleString(lang, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  private toast(
    key: string,
    actionKey?: string,
    undo?: () => void,
    duration = 4000,
    params?: Record<string, unknown>,
  ): void {
    const message = this.t.instant(key, params);
    if (undo && actionKey) {
      this.uiFeedback.toast.action(message, this.t.instant(actionKey), undo, { duration });
    } else {
      this.uiFeedback.toast.info(message, { duration });
    }
  }
}
